import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Slide from '@mui/material/Slide';
import React, { memo, useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';

import dayjs, { Dayjs } from 'dayjs';

import { TransitionProps } from '@mui/material/transitions';
import DatePickerCustom from 'src/components/common/field/DatePickerCustom';
import DropDown from 'src/components/common/field/DropDown';
import DropDownMultiSelect from 'src/components/common/field/DropDownMultiSelect';
import InputSearch from 'src/components/common/field/InputSearch';
import InputTextAreaAutosize from 'src/components/common/field/InputTextAreaAutosize';
import TimePickerCustom from 'src/components/common/field/TimePickerCustom';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import InputSearchCustom from 'src/components/common/field/InputSearchCustom';

import { BaseDataType } from 'src/components/reservationList/TypeCustom';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken, getUserLogged } from 'src/utils/auth';
import { getField, getToday, isEmpty } from 'src/common/helper';
import { useNotify } from 'src/context/ContextNotify';

const typographyLabel = {
  fontSize: '12px',
  lineHeight: '14.4px',
  fontWeight: 400,
  color: '#00000080',
  mb: '4px',
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
  '&.MuiInputLabel-root': {
    color: '#434154',
    transform: 'translate(14px, 16px) scale(1)',
  },
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500',
};

const currentDate: string = dayjs().format('YYYY-MM-DD');

const defaultAdd = {
  id: null,
  doctorid: null,
  type: null,
  date: null,
  starttime: '',
  finishtime: null,
  visittime: null,
  paitenname: '',
  patientid: '',
  paitenhistory: '',
  partcategories: 0,
  parts: [],
  status: null,
  person: null,
  note: '',
};

type dataAddType = {
  id: number | null;
  doctorid: string | null;
  type: string | null;
  date: string | null;
  starttime: string | null;
  finishtime: string | null;
  visittime: string | null;
  paitenname: string;
  patientid: string;
  paitenhistory: string;
  partcategories: number | null;
  parts: number[];
  status: number | null;
  person: number | null;
  note: string;
};

export type dataType = {
  typeAdd: boolean;
  positionData: any;
  reserveEdit: any;
  patientEdit: any;
  indexColumn: number;
};

interface DialogAddReserveProps {
  open: boolean;
  onClose: (isRefreshList?: boolean) => void;
  onSave?: () => void;
  data: dataType;
}

const defaultError = {
  starttime: true,
  finishtime: true,
  thedate: true,
};

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

const DialogAddReserve: React.FC<DialogAddReserveProps> = (props) => {
  const notifyContext = useNotify();
  const [dataAdd, setDataAdd] = useState<dataAddType>(defaultAdd);
  const [baseData, setBaseData] = useState<BaseDataType>();
  const [errors, setErrors] = useState(defaultError);

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
      }))
      : [];

    return converted;
  };

  const convertItemTypePart = (data: any[]) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item.id),
        name: item.part,
        parentId: String(item.categoryid),
      }))
      : [];
    return converted;
  };

  const getBaseData = async () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      getAPI(`/reserve/base-data?token=${token}`)
        .then((res: any) => {
          if (res.code === 200) {
            const doctorsFilter = convertItemType(
              res.data.doctors,
              'id',
              'name'
            );
            const opsFilter = convertItemType(res.data.ops, 'id', 'category');
            setBaseData((prevState) => ({
              ...prevState,
              doctors: [...doctorsFilter],
              ops: [...opsFilter],
              status: [
                ...convertItemType(res.data.status, 'value', 'label'),
              ].filter((sta) => {
                if (props.data.typeAdd) {
                  return sta.id === '1' || sta.id === '9';
                }
                return sta;
              }),
              part_categories: [
                ...convertItemType(res.data.part_categories, 'id', 'category'),
              ],
              parts: [...convertItemTypePart(res.data.parts)],
              machines: [...convertItemType(res.data.machines, 'id', 'name')],
              users: [...convertItemType(res.data.users, 'userid', 'name')],
            }));
            // if (props.data.typeAdd && doctorsFilter.length > 0) {
            //   setDataAdd((prevState) => ({
            //     ...prevState,
            //     doctorid: doctorsFilter[0].id,
            //   }));
            // }
            // if (props.data.typeAdd && opsFilter.length > 0) {
            //   setDataAdd((prevState) => ({
            //     ...prevState,
            //     type: opsFilter[0].id,
            //   }));
            // }
          }
        })
        .finally(() => { });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const handleClose = () => {
    setDataAdd(defaultAdd);
    props.onClose();
  };

  const handleChange =
    (name: string) => (id: number | string | number[] | null) => {
      setDataAdd((prevState) => ({
        ...prevState,
        [name]: id,
      }));
    };

  const handleChangeDropdownMulti = (itemIds: number[]) => {
    handleChange('parts')(itemIds);
  };

  const handleChangeDate = (date: Dayjs | null) => {
    const dateFormat = date ? date.format('YYYY/MM/DD') : null;
    handleChange('date')(dateFormat);
  };

  const handleChangeTime = (name: string) => (date: Dayjs | null) => {
    const timeFormat = date ? date.format('HH:mm') : null;

    if (name === 'starttime' && timeFormat) {
      const finishTime = date ? date.add(30, 'minutes').format('HH:mm') : null;
      handleChange('finishtime')(finishTime);
    }

    handleChange(name)(timeFormat);
  };

  const validateDate = () => {
    if (isEmpty(dataAdd.date)) {
      setErrors({ ...defaultError, thedate: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '日付を選択して下さい',
        type: 'error'
      });
      return false;
    }
    if (isEmpty(dataAdd.starttime)) {
      setErrors({ ...defaultError, starttime: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '開始時間は必須です',
        type: 'error'
      });
      return false;
    }
    if (isEmpty(dataAdd.finishtime)) {
      setErrors({ ...defaultError, finishtime: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '終了時間は必須です',
        type: 'error'
      });
      return false;
    }
    setErrors(defaultError);
    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });
    return true;
  };

  const prepareDataSave = () => {
    let data: any = {
      id: props.data.typeAdd ? null : dataAdd.id,
      doctorid: getField(dataAdd, 'doctorid', 0),
      typeid: Number(dataAdd.type),
      status: Number(dataAdd.status),
      thedate: dataAdd.date,
      starttime: dataAdd.starttime,
      finishtime: dataAdd.finishtime,
      visittime: dataAdd.visittime,
      fullname: dataAdd.paitenname,
      partid: dataAdd.parts,
      patient_note: dataAdd.paitenhistory,
      note: dataAdd.note,
      quoteid: 0,
      person: dataAdd.person,
      caseid: 0
    };

    if (dataAdd.patientid !== '') {
      data['patientid'] = dataAdd.patientid;
    }

    if ([1, 2].includes(getTypeId()) && dataAdd.type === '1') {
      data['opcategoryid'] = 1;
    }

    if (getTypeId() === 3) {
      const machine = getField(props.data.positionData, 'machine', {});

      data = {
        ...data,
        doctorid: 0,
        devicedoctorid: dataAdd.doctorid,
        machineid: getField(machine, 'id', 0)
      };
    }

    return data;
  };

  const handleButtonClick = () => {
    if (!validateDate()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }
      let data: any = prepareDataSave();

      if (!props.data.typeAdd) {
        data = {
          ...data,
          customize1: null,
          customize2: null,
          customize3: null,
          customize4: null,
          customize5: null,
          devicedoctorid: "",
          inputtime: "",
          inputuser: dataAdd.person,
          kkparentid: 0,
          machineid: 0,
          updatetime: null,
          updateuser: null,
          visitnote: null,
          terms: ""
        };
      }

      postAPI(`/reserve/save?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            props.onClose(true);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        })
        .finally(() => { });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getTypeId = () => {
    const positionData = props.data.positionData;

    switch (getField(positionData, 'typeColumn', '')) {
      case 'doctor':
        {
          const doctor = getField(props.data.positionData, 'doctor', {});

          return getField(doctor, 'op', false) ? 1 : 2;
        }
      case 'machine':
        return 3;
      case 'nurse':
        return 4;
      case 'reception':
        return 5;
      default:
        return 1;
    }
  }

  const setSelectDefault = () => {
    const status = getField(baseData, 'status', []);
    const statusSelect = status.find((item: any) => item.name === '仮予約');

    const userLogin = getUserLogged();
    if (userLogin && userLogin.user && userLogin.user.id) {
      setDataAdd((prevState) => ({
        ...prevState,
        person: userLogin.user.id,
      }));
    }

    const doctor = getField(props.data.positionData, 'doctor', {});

    setDataAdd((prevState) => ({
      ...prevState,
      status: getField(statusSelect, 'id', null),
      doctorid: getField(props.data.positionData, 'typeColumn', '') === 'doctor' ?
        String(getField(doctor, 'id', null)) : null,
      starttime: getField(props.data.positionData, 'starttime', ''),
      finishtime: dayjs(getToday() + ' '
        + getField(props.data.positionData, 'starttime', '')).add(30, 'minutes').format('HH:mm'),
      date: getField(props.data.positionData, 'day', getToday()),
      type: String(getTypeId())
    }));
  };

  const handleChangeInputSearch = (value: any | undefined) => {
    setDataAdd((prevState) => ({
      ...prevState,
      patientid: getField(value, 'patientno', '')
    }));
  };

  const setSelectEdit = () => {
    let categoryPart = 0;
    const reserveEdit = props.data.reserveEdit;
    const patientEdit = props.data.patientEdit;

    if (reserveEdit.partid.length > 0) {
      const partid = reserveEdit.partid[0];
      const part: any = baseData?.parts?.filter(
        (part) => String(part.id) === String(partid)
      );
      categoryPart = part?.length > 0 ? part[0].parentId : 0;
    }

    setDataAdd((prevState) => ({
      ...prevState,
      id: getField(reserveEdit, 'id', null),
      doctorid: String(getField(reserveEdit, 'doctorid', null)),
      type: String(getField(reserveEdit, 'typeid', null)),
      date: getField(reserveEdit, 'thedate', null),
      starttime: getField(reserveEdit, 'starttime', null),
      finishtime: getField(reserveEdit, 'finishtime', null),
      visittime: getField(reserveEdit, 'visittime', null),
      patientid: getField(patientEdit, 'id', ''),
      paitenname: getField(patientEdit, 'fullname', ''),
      parts: getField(reserveEdit, 'partid', null),
      partcategories: categoryPart,
      status: getField(reserveEdit, 'status', null),
      person: getField(reserveEdit, 'person', null),
      note: getField(reserveEdit, 'note', ''),
    }));
  };

  useEffect(() => {
    if (props.open) {
      getBaseData();
      if (!props.data.typeAdd) {
        setSelectEdit();
      } else {
        setSelectDefault();
      }
    } else {
      setDataAdd(defaultAdd);
    }

    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      // BackdropProps={{ invisible: true }}
      // slotProps={{ backdrop: { invisible: true } }}
      TransitionComponent={Transition}
      sx={{
        position: 'fixed !important',
        top: '0px !important',
        right: '0px !important',
        left: 'auto',
        bottom: 'auto',
        p: '0px',
        // zIndex: 2147483647,
        '& .MuiDialog-paper': {
          m: '0px !important',
          width: '302px',
          height: '100vh',
          bgcolor: '#FAFDFF',
          borderRadius: '16px 0px 0px 16px',
        },
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '16px',
              fontWeight: 700,
              alignContent: 'center',
            }}
          >
            新規予約
          </Typography>
          <IconButton
            aria-label='toggle password visibility'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-arrow-right-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '24px',
          pr: '10px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden',
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 150px)', borderWidth: '18px' }}
        >
          <Stack
            spacing='10px'
            sx={{
              pr: '14px',
              '& .MuiFormControl-root': {
                height: '32px',
              },
              '& .MuiOutlinedInput-input, & .MuiInputBase-root.MuiOutlinedInput-root.MuiSelect-root .MuiInputBase-input':
              {
                py: '7px !important',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                height: '32px',
                top: 0,
              },
              '& .MuiOutlinedInput-notchedOutline legend': {
                lineHeight: 0,
              },
              '& .MuiInputLabel-root': {
                top: '-7px',
              },
              '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root':
              {
                height: '32px',
              },
            }}
          >
            {![4, 5].includes(getTypeId()) && (
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  医師
                </Typography>
                <DropDown
                  selected={dataAdd.doctorid}
                  idSelect='doctor-select'
                  // placeholder='選択してください'
                  items={baseData?.doctors}
                  onChange={handleChange('doctorid')}
                  sxInput={{ width: '100%' }}
                  sxLabel={sxLableDropDown}
                />
              </Stack>
            )}

            {![3, 4, 5].includes(getTypeId()) && (
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  予約タイプ
                </Typography>
                <DropDown
                  selected={dataAdd.type}
                  idSelect='ops-select'
                  placeholder='選択してください'
                  items={baseData?.ops}
                  onChange={handleChange('type')}
                  sxInput={{ width: '100%' }}
                  sxLabel={sxLableDropDown}
                />
              </Stack>
            )}

            <Stack
              sx={{
                '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                {
                  borderColor: !errors.thedate ? '#d32f2f !important' : '',
                },
              }}
            >
              <Typography sx={{ ...typographyLabel }}>
                予約日
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              <DatePickerCustom
                value={dayjs(dataAdd.date)}
                onChange={handleChangeDate}
              />
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>
                時間
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <Stack
                direction={'row'}
                justifyContent='left'
                sx={{
                  '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root':
                  {
                    width: '116px',
                  },
                }}
              >
                <Stack
                  sx={{
                    '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: !errors.starttime
                        ? '#d32f2f !important'
                        : '',
                    },
                  }}
                >
                  <TimePickerCustom
                    value={dayjs(
                      dataAdd.date
                        ? dataAdd.date + ' ' + dataAdd.starttime
                        : currentDate + ' ' + dataAdd.starttime
                    )}
                    onChange={handleChangeTime('starttime')}
                  />
                </Stack>
                <Typography
                  sx={{
                    ...typographyLabel,
                    display: 'flex',
                    alignItems: 'center',
                    mx: '5px',
                    width: 'unset',
                  }}
                >
                  〜
                </Typography>
                <Stack
                  sx={{
                    '& .MuiFormControl-root.MuiTextField-root .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline':
                    {
                      borderColor: !errors.finishtime
                        ? '#d32f2f !important'
                        : '',
                    },
                  }}
                >
                  <TimePickerCustom
                    value={dayjs(
                      dataAdd.date
                        ? dataAdd.date + ' ' + dataAdd.finishtime
                        : currentDate + ' ' + dataAdd.finishtime
                    )}
                    onChange={handleChangeTime('finishtime')}
                  />
                </Stack>
              </Stack>
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>来院時間</Typography>
              <TimePickerCustom
                value={dayjs(dataAdd.date + ' ' + dataAdd.visittime)}
                onChange={handleChangeTime('visittime')}
              />
            </Stack>

            <Stack>
              <Stack>
                <Typography sx={{ ...typographyLabel }}>
                  患者情報（どちらか）
                </Typography>
              </Stack>
              <Stack direction={'row'}>
                <InputSearchCustom
                  labelInput='患者名'
                  name='fullname'
                  value={dataAdd.paitenname}
                  onChange={handleChangeInputSearch}
                  sx={{
                    '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
                      py: '7px',
                      pr: '15px !important',
                      width: '120px',
                      height: '32px',
                    },
                    mr: '10px',
                    width: '120px',
                    height: '32px'
                  }}
                  readonly={!props.data.typeAdd}
                />

                <InputSearch
                  labelInput='患者ID'
                  name='patientid'
                  value={dataAdd.patientid}
                  onChange={() => { }}
                  sx={{ width: '120px', height: '32px' }}
                />
              </Stack>
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>希望施術</Typography>
              <Stack spacing={'10px'}>
                <DropDown
                  selected={dataAdd.partcategories}
                  idSelect='part-category-select'
                  placeholder='選択してください'
                  items={baseData?.part_categories}
                  onChange={handleChange('partcategories')}
                  sxInput={{ width: '100%' }}
                  sxLabel={sxLableDropDown}
                />
                <DropDownMultiSelect
                  selected={dataAdd.parts}
                  idSelect='parts-select'
                  placeholder='選択してください'
                  items={baseData?.parts}
                  parentId={dataAdd.partcategories}
                  onChange={handleChangeDropdownMulti}
                  sxInput={{ width: '100%' }}
                  sxLabel={sxLableDropDown}
                />
              </Stack>
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>ステータス</Typography>
              <DropDown
                selected={dataAdd.status}
                idSelect='status-select'
                placeholder='選択してください'
                items={baseData?.status}
                onChange={handleChange('status')}
                sxInput={{ width: '100%' }}
                sxLabel={sxLableDropDown}
              />
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>
                担当者
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              <DropDown
                selected={dataAdd.person}
                idSelect='user-select'
                placeholder='選択してください'
                items={baseData?.users}
                onChange={handleChange('person')}
                sxInput={{ width: '100%' }}
                sxLabel={sxLableDropDown}
              />
            </Stack>

            <Stack>
              <Typography sx={{ ...typographyLabel }}>備考</Typography>
              <InputTextAreaAutosize
                value={dataAdd.note}
                onChange={handleChange('note')}
                width='100%'
                height='104px'
              />
            </Stack>
          </Stack>
        </SimpleBar>
      </DialogContent>
      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px',
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} />
        <BtnGradient text='登録' handleButtonClick={handleButtonClick} />
      </DialogActions>
    </Dialog>
  );
};

export default memo(DialogAddReserve);
